// ============================================================
// Auth Service — 健壯的登入/登出封裝，處理 profile 缺失與緩存問題
// ============================================================
import { supabase } from '@/lib/supabase'
import type { Profile } from '@/lib/supabase'
import { getProfile } from './supabaseService'

const AUTH_KEY = 'jishi_auth_user'

/** 判斷是否為 Refresh Token 無效錯誤 */
function isRefreshTokenError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const msg = (err as { message?: string }).message || ''
  return (
    msg.includes('Invalid Refresh Token') ||
    msg.includes('Refresh Token Not Found') ||
    msg.includes('refresh_token') ||
    (err as { code?: string }).code === 'refresh_token_not_found'
  )
}

/** 清理所有本地 auth 緩存 */
export function clearAuthCache() {
  localStorage.removeItem(AUTH_KEY)
  // Also clear any legacy keys
  localStorage.removeItem('jishi_auth_token')
  localStorage.removeItem('jishi_user')
}

/** 安全清除 auth 狀態（本地） */
export async function safeClearAuthLocal() {
  try {
    await supabase.auth.signOut({ scope: 'local' })
  } catch {
    // ignore
  }
  clearAuthCache()
}

/** 確保用戶的 profile 存在；如果不存在則自動創建 */
export async function ensureProfile(userId: string, userMeta?: Record<string, unknown>): Promise<Profile | null> {
  let profile = await getProfile(userId)
  if (profile) return profile

  // Profile missing — create via upsert (RLS must allow insert for own id)
  const fallback: Omit<Profile, 'id'> & { id: string } = {
    id: userId,
    name: (userMeta?.name as string) || (userMeta?.email as string)?.split('@')[0] || '用戶',
    email: (userMeta?.email as string) || '',
    phone: (userMeta?.phone as string) || null,
    role: (userMeta?.role as 'admin' | 'user') || 'user',
    level: (userMeta?.level as string) || '普通分銷',
    balance: 0,
    pending_balance: 0,
    kyc_status: '未認證',
    created_at: new Date().toISOString(),
    avatar: null,
    notes: null,
    kyc_real_name: null,
    kyc_id_number: null,
    kyc_front_photo: null,
    kyc_back_photo: null,
    kyc_holding_photo: null,
    kyc_submitted_at: null,
    kyc_reviewed_at: null,
    kyc_admin_note: null,
  }

  const { data, error } = await supabase
    .from('profiles')
    .upsert([fallback as Record<string, unknown>], { onConflict: 'id' })
    .select()
    .single()

  if (error) {
    console.warn('[authService] Failed to auto-create profile:', error.message)
    return null
  }

  return data as Profile
}

/** 安全登入：處理所有邊界情況 */
export async function safeSignIn(email: string, password: string): Promise<Profile> {
  clearAuthCache()

  const { data, error } = await supabase.auth.signInWithPassword({ email, password })
  if (error) {
    console.error('[authService] signInWithPassword error:', error.message, error.status)
    throw error
  }
  if (!data.user) {
    throw new Error('登入成功但無法取得用戶資訊')
  }

  // Ensure profile exists (creates if missing)
  const profile = await ensureProfile(data.user.id, {
    ...data.user.user_metadata,
    email: data.user.email,
  })

  if (!profile) {
    throw new Error('登入成功但無法讀取或建立用戶資料，請聯繫管理員')
  }

  localStorage.setItem(AUTH_KEY, JSON.stringify(profile))
  return profile
}

/** 安全登出 */
export async function safeSignOut() {
  await supabase.auth.signOut()
  clearAuthCache()
}

/** 安全取得當前 session（攔截 Refresh Token 錯誤） */
export async function safeGetSession() {
  try {
    const { data, error } = await supabase.auth.getSession()
    if (error && isRefreshTokenError(error)) {
      console.warn('[authService] Refresh token invalid during getSession:', error.message)
      await safeClearAuthLocal()
      return { data: { session: null }, error: null }
    }
    return { data, error }
  } catch (err) {
    if (isRefreshTokenError(err)) {
      console.warn('[authService] Refresh token error caught:', err)
      await safeClearAuthLocal()
      return { data: { session: null }, error: null }
    }
    throw err
  }
}